
/**
 * Video Queue Manager
 * 
 * Manages a queue for video loading to prevent too many concurrent requests.
 * This helps improve overall app performance by limiting network congestion.
 */
import { isPrerender, isMobileDevice, isLowEndDevice } from "./environment";

// Define the types we'll need
export interface QueuedVideo {
  id: string;
  url: string | undefined;
  priority: 'high' | 'medium' | 'low';
  isVisible: boolean;
  isInViewport: boolean;
  isUserRequested: boolean;
  onLoadStart: () => void;
  onLoadComplete: () => void;
  onLoadError: (error: Error) => void;
  status: 'queued' | 'loading' | 'loaded' | 'error';
  addedAt: number;
}

// Configuration for different device types
const MOBILE_CONFIG = {
  maxConcurrentLoads: 1, // Only load one video at a time on mobile
  highPriorityMaxConcurrent: 1, // Additional slot for high priority videos
  stallTimeout: 10000, // 10 seconds timeout for stalled loads on mobile
  reserveForViewport: 1, // Always reserve 1 slot for videos in viewport
};

const DESKTOP_CONFIG = {
  maxConcurrentLoads: 3, // Default to 3 concurrent loads on desktop
  highPriorityMaxConcurrent: 1, // Additional slot for high priority videos
  stallTimeout: 15000, // 15 seconds timeout for stalled loads
  reserveForViewport: 1, // Always reserve 1 slot for videos in viewport
};

// Function to get device-specific configuration
const getDeviceConfig = () => {
  if (isMobileDevice()) {
    return MOBILE_CONFIG;
  }
  
  // For low-end desktop devices, use a more conservative config
  if (isLowEndDevice()) {
    return {
      ...DESKTOP_CONFIG,
      maxConcurrentLoads: 2,
    };
  }
  
  return DESKTOP_CONFIG;
};

class VideoQueueManager {
  private queue: QueuedVideo[] = [];
  private loading: Map<string, QueuedVideo> = new Map();
  private config = getDeviceConfig();
  private processingQueue: boolean = false;
  private static instance: VideoQueueManager;
  private priorityBoostMap: Map<string, number> = new Map();
  private lastConfigUpdate: number = Date.now();

  private constructor() {
    // Private constructor to enforce singleton
    this.setupPeriodicQueueCheck();
  }

  // Singleton pattern to ensure we have only one queue manager
  public static getInstance(): VideoQueueManager {
    if (!VideoQueueManager.instance) {
      VideoQueueManager.instance = new VideoQueueManager();
      console.log("VideoQueueManager: Instance created with config:", VideoQueueManager.instance.config);
    }
    return VideoQueueManager.instance;
  }

  // Configure the queue manager
  public configure(options: Partial<typeof DESKTOP_CONFIG>): void {
    this.config = {
      ...this.config,
      ...options,
    };
    console.log("VideoQueueManager: Configuration updated", this.config);
  }

  // Refresh configuration based on device
  public refreshDeviceConfig(): void {
    // Don't refresh too frequently
    if (Date.now() - this.lastConfigUpdate < 60000) {
      return;
    }
    
    this.config = getDeviceConfig();
    this.lastConfigUpdate = Date.now();
    console.log("VideoQueueManager: Configuration refreshed for device", this.config);
  }

  // Get current queue stats
  public getStats() {
    this.refreshDeviceConfig(); // Make sure we have the right config
    return {
      queueLength: this.queue.length,
      loading: this.loading.size,
      maxConcurrent: this.config.maxConcurrentLoads,
      isMobile: isMobileDevice(),
      isLowEnd: isLowEndDevice(),
    };
  }

  // Add a video to the queue
  public enqueue(video: Omit<QueuedVideo, 'status' | 'addedAt'>): string {
    if (!video.url) {
      console.warn("VideoQueueManager: Attempted to queue a video with no URL");
      return "";
    }
    
    // If this is a prerendered environment, immediately call the load complete callback
    if (isPrerender()) {
      console.log("VideoQueueManager: Prerender environment detected, skipping queue");
      setTimeout(() => {
        video.onLoadComplete();
      }, 0);
      return video.id;
    }

    // Check if this video is already in queue
    const existingQueueIndex = this.queue.findIndex(v => v.id === video.id || v.url === video.url);
    
    // Check if this video is already loading
    if (this.loading.has(video.id)) {
      console.log(`VideoQueueManager: Video ${video.id} already loading`);
      return video.id;
    }
    
    // If video is already in queue, update its priority and visibility
    if (existingQueueIndex !== -1) {
      const existingVideo = this.queue[existingQueueIndex];
      
      // Update the video's priority and visibility
      this.queue[existingQueueIndex] = {
        ...existingVideo,
        priority: this.getHighestPriority(existingVideo.priority, video.priority),
        isVisible: existingVideo.isVisible || video.isVisible,
        isInViewport: existingVideo.isInViewport || video.isInViewport,
        isUserRequested: existingVideo.isUserRequested || video.isUserRequested,
      };
      
      console.log(`VideoQueueManager: Updated video ${video.id} in queue`);
      
      // Re-sort the queue to reflect the new priorities
      this.sortQueue();
      
      return video.id;
    }

    // Otherwise, add it as a new item
    const newQueueItem: QueuedVideo = {
      ...video,
      status: 'queued',
      addedAt: Date.now(),
    };
    
    this.queue.push(newQueueItem);
    console.log(`VideoQueueManager: Added video ${video.id} to queue. Queue length: ${this.queue.length}`);
    
    // Sort the queue by priority
    this.sortQueue();
    
    // Start processing the queue
    this.processQueue();
    
    return video.id;
  }

  // Remove a video from the queue or loading list
  public dequeue(id: string): boolean {
    // Try to remove from the queue first
    const queueIndex = this.queue.findIndex(v => v.id === id);
    if (queueIndex !== -1) {
      this.queue.splice(queueIndex, 1);
      console.log(`VideoQueueManager: Removed video ${id} from queue`);
      return true;
    }
    
    // If not in queue, try to remove from loading
    if (this.loading.has(id)) {
      this.loading.delete(id);
      console.log(`VideoQueueManager: Removed video ${id} from loading`);
      this.processQueue(); // Process next item if we freed up a slot
      return true;
    }
    
    return false;
  }

  // Boost priority of a video (e.g., user interaction)
  public boostPriority(id: string): void {
    // Find in queue
    const queueIndex = this.queue.findIndex(v => v.id === id);
    if (queueIndex !== -1) {
      this.queue[queueIndex].priority = 'high';
      this.queue[queueIndex].isUserRequested = true;
      this.sortQueue();
      console.log(`VideoQueueManager: Boosted priority for video ${id}`);
    }

    // Track number of boosts
    const currentBoosts = this.priorityBoostMap.get(id) || 0;
    this.priorityBoostMap.set(id, currentBoosts + 1);
    
    // If video is not in queue but user requested it multiple times, 
    // we should try to load it immediately
    if (currentBoosts > 2 && !this.loading.has(id)) {
      console.log(`VideoQueueManager: User repeatedly requested video ${id}, processing queue immediately`);
      this.processQueue(true);
    }
  }

  // Update visibility state of a video
  public updateVisibility(id: string, isVisible: boolean, isInViewport: boolean): void {
    // Update in queue
    const queueIndex = this.queue.findIndex(v => v.id === id);
    if (queueIndex !== -1) {
      this.queue[queueIndex].isVisible = isVisible;
      this.queue[queueIndex].isInViewport = isInViewport;
      this.sortQueue();
    }
    
    // If video is now in viewport, we should process queue to prioritize it
    if (isInViewport) {
      this.processQueue();
    }
  }

  // Private methods
  private getHighestPriority(a: QueuedVideo['priority'], b: QueuedVideo['priority']): QueuedVideo['priority'] {
    const priorities = { 'high': 3, 'medium': 2, 'low': 1 };
    return priorities[a] > priorities[b] ? a : b;
  }

  private sortQueue(): void {
    this.queue.sort((a, b) => {
      // First priority: User requested videos
      if (a.isUserRequested !== b.isUserRequested) {
        return a.isUserRequested ? -1 : 1;
      }
      
      // Second priority: Viewport visibility
      if (a.isInViewport !== b.isInViewport) {
        return a.isInViewport ? -1 : 1;
      }
      
      // Third priority: General visibility
      if (a.isVisible !== b.isVisible) {
        return a.isVisible ? -1 : 1;
      }
      
      // Fourth priority: Priority level
      const priorities = { 'high': 3, 'medium': 2, 'low': 1 };
      if (priorities[a.priority] !== priorities[b.priority]) {
        return priorities[b.priority] - priorities[a.priority];
      }
      
      // Last priority: First come, first served
      return a.addedAt - b.addedAt;
    });
  }

  private processQueue(force: boolean = false): void {
    // Prevent concurrent processing
    if (this.processingQueue && !force) return;
    
    this.processingQueue = true;
    
    try {
      // Refresh device configuration if needed
      this.refreshDeviceConfig();
      
      // Check how many slots we can use
      const availableSlots = this.config.maxConcurrentLoads - this.loading.size;
      
      // If no slots available, return
      if (availableSlots <= 0) {
        console.log("VideoQueueManager: No slots available for loading videos");
        return;
      }
      
      console.log(`VideoQueueManager: Processing queue. Available slots: ${availableSlots}, Queue length: ${this.queue.length}`);
      
      // Count videos in viewport that are queued
      const viewportVideosCount = this.queue.filter(v => v.isInViewport).length;
      
      // Reserve slots for viewport videos if needed
      let slotsToUse = availableSlots;
      if (viewportVideosCount > 0 && this.config.reserveForViewport > 0) {
        const reservedSlots = Math.min(this.config.reserveForViewport, viewportVideosCount);
        if (reservedSlots >= availableSlots) {
          // We need all slots for viewport videos
          const viewportVideos = this.queue.filter(v => v.isInViewport).slice(0, slotsToUse);
          
          // Start loading these videos
          viewportVideos.forEach(video => this.startLoading(video));
          return;
        }
      }
      
      // Take videos from the queue up to the available slots
      const videosToLoad = this.queue.slice(0, slotsToUse);
      
      // Start loading these videos
      videosToLoad.forEach(video => this.startLoading(video));
    } finally {
      this.processingQueue = false;
    }
  }

  private startLoading(video: QueuedVideo): void {
    // Remove from queue
    this.queue = this.queue.filter(v => v.id !== video.id);
    
    // Add to loading
    this.loading.set(video.id, {
      ...video,
      status: 'loading',
    });
    
    console.log(`VideoQueueManager: Started loading video ${video.id}`);
    
    // Call the onLoadStart callback
    video.onLoadStart();
    
    // Set a timeout to detect stalled loads
    setTimeout(() => {
      if (this.loading.has(video.id)) {
        console.warn(`VideoQueueManager: Loading of video ${video.id} taking too long, might be stalled`);
        
        // We don't automatically cancel it, as it might still complete
        // Instead we start loading another video to ensure progress
        this.processQueue();
      }
    }, this.config.stallTimeout);
  }

  // Mark a video as loaded
  public markLoaded(id: string): void {
    if (this.loading.has(id)) {
      const video = this.loading.get(id)!;
      
      // Remove from loading
      this.loading.delete(id);
      
      // Call the onLoadComplete callback
      video.onLoadComplete();
      
      console.log(`VideoQueueManager: Video ${id} loaded successfully`);
      
      // Process next video in queue
      this.processQueue();
    }
  }

  // Mark a video as failed
  public markFailed(id: string, error: Error): void {
    if (this.loading.has(id)) {
      const video = this.loading.get(id)!;
      
      // Remove from loading
      this.loading.delete(id);
      
      // Call the onLoadError callback
      video.onLoadError(error);
      
      console.error(`VideoQueueManager: Video ${id} failed to load`, error);
      
      // Process next video in queue
      this.processQueue();
    }
  }

  // Check for stalled loads periodically
  private setupPeriodicQueueCheck(): void {
    setInterval(() => {
      // Look for stalled loads (those taking too long)
      const now = Date.now();
      
      for (const [id, video] of this.loading.entries()) {
        if (now - video.addedAt > this.config.stallTimeout * 1.5) {
          console.warn(`VideoQueueManager: Video ${id} has been loading for too long, considering it stalled`);
          
          // Handle the stalled video (don't auto-cancel, but make room for another video)
          this.processQueue();
        }
      }
      
      // Process queue to ensure we're using all available slots
      if (this.queue.length > 0 && this.loading.size < this.config.maxConcurrentLoads) {
        this.processQueue();
      }
    }, this.config.stallTimeout);
  }
}

// Helper for detecting if environment is prerender/SSR
export const isVideoQueueSupported = (): boolean => {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
};

// Export the singleton instance
export const videoQueue = VideoQueueManager.getInstance();

// Export the default config for reference
export const defaultVideoQueueConfig = {
  mobile: { ...MOBILE_CONFIG },
  desktop: { ...DESKTOP_CONFIG }
};

// Add a debug function to help track mobile video loading issues
export const debugVideoLoading = (videoId: string, message: string) => {
  console.log(`Video Loading Debug [${videoId}]: ${message}`);
};
